import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription, hasTier } from '../context/SubscriptionContext';
import { Navigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import TierGate from '../components/TierGate';
import { portfolioApi } from '../services/api';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Briefcase, Plus, TrendUp, TrendDown, ChartPieSlice, ShieldCheck, Trash } from '@phosphor-icons/react';
import { formatINR, formatPercent } from '../utils/formatters';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTOR_COLORS = ['#2563EB', '#00FF66', '#A855F7', '#FBBF24', '#FF3366', '#06B6D4', '#F97316', '#EC4899', '#10B981', '#8B5CF6', '#F59E0B'];

const PIE_TOOLTIP_STYLE = { background: '#12141A', border: '1px solid rgba(255,255,255,0.15)' };
const PIE_TOOLTIP_LABEL = { color: '#94A3B8', fontFamily: 'JetBrains Mono' };

export default function Portfolio() {
  const { user, loading } = useAuth();
  const { tier, loading: subLoading } = useSubscription();
  const [analytics, setAnalytics] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ symbol: '', quantity: '', avg_price: '' });

  const loadAnalytics = useCallback(async () => {
    if (!hasTier(tier, 'pro')) return;
    try {
      const { data } = await axios.get(`${API}/portfolio/analytics`, { withCredentials: true });
      setAnalytics(data);
    } catch {
      setAnalytics(null);
    }
  }, [tier]);

  useEffect(() => {
    if (user && hasTier(tier, 'pro')) loadAnalytics();
    const interval = setInterval(() => user && hasTier(tier, 'pro') && loadAnalytics(), 15000);
    return () => clearInterval(interval);
  }, [user, tier, loadAnalytics]);

  const handleAddHolding = async (e) => {
    e.preventDefault();
    try {
      await portfolioApi.addHolding({
        symbol: formData.symbol.toUpperCase(),
        quantity: parseInt(formData.quantity),
        avg_price: parseFloat(formData.avg_price),
      });
      setFormData({ symbol: '', quantity: '', avg_price: '' });
      setShowAddForm(false);
      loadAnalytics();
    } catch (e) {
      console.error('add holding failed', e);
    }
  };

  const handleRemoveHolding = async (symbol) => {
    if (!window.confirm(`Remove all ${symbol} holdings from your portfolio?`)) return;
    try {
      await axios.delete(`${API}/portfolio/holdings/${symbol}`, { withCredentials: true });
      toast.success(`${symbol} removed from portfolio`);
      loadAnalytics();
    } catch (e) {
      console.error('remove holding failed', e);
      toast.error(`Failed to remove ${symbol}`);
    }
  };

  if (loading || subLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}><div className="text-white">Loading...</div></div>;
  if (!user) return <Navigate to="/login" />;

  if (!hasTier(tier, 'pro')) {
    return (
      <Layout>
        <TierGate requiredTier="pro" feature="Portfolio Analytics">
          <div />
        </TierGate>
      </Layout>
    );
  }

  const hasHoldings = analytics && analytics.holdings && analytics.holdings.length > 0;
  const pnlPositive = analytics && analytics.total_pnl >= 0;
  const dayPositive = analytics && analytics.day_change >= 0;

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-8" data-testid="portfolio-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Briefcase size={40} weight="duotone" className="text-[#2563EB]" />
            <div>
              <h1 className="text-4xl font-heading font-black tracking-tighter text-white">Portfolio</h1>
              <p className="text-sm text-[#94A3B8] mt-1">Live P&L • Auto-refreshes every 15s</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#2563EB] hover:bg-blue-600 text-white px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
            data-testid="add-holding-button"
          >
            <Plus size={20} weight="bold" />
            Add Holding
          </button>
        </div>

        {showAddForm && (
          <div className="bg-[#12141A] border border-white/10 p-6 mb-6" data-testid="add-holding-form">
            <h3 className="text-lg font-heading font-bold text-white mb-4">Add New Holding</h3>
            <form onSubmit={handleAddHolding} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Symbol</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                  placeholder="RELIANCE"
                  data-testid="holding-symbol-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Quantity</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  required
                  min="1"
                  className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                  placeholder="10"
                  data-testid="holding-quantity-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Avg Price ₹</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.avg_price}
                  onChange={(e) => setFormData({ ...formData, avg_price: e.target.value })}
                  required
                  min="0"
                  className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                  placeholder="2850.00"
                  data-testid="holding-price-input"
                />
              </div>
              <div className="md:col-span-3">
                <button type="submit" className="bg-[#2563EB] hover:bg-blue-600 text-white px-4 py-2 text-sm font-medium transition-colors" data-testid="submit-holding-button">
                  Add to Portfolio
                </button>
              </div>
            </form>
          </div>
        )}

        {hasHoldings ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="portfolio-summary">
              <div className="bg-[#12141A] border border-white/10 p-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Current Value</div>
                <div className="text-3xl font-mono font-semibold text-white">{formatINR(analytics.current_value)}</div>
                <div className="text-xs text-[#64748B] font-mono mt-1">Invested: {formatINR(analytics.total_invested)}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Total P&L</div>
                <div className={`text-3xl font-mono font-semibold flex items-center gap-2 ${pnlPositive ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>
                  {pnlPositive ? <TrendUp size={24} weight="bold" /> : <TrendDown size={24} weight="bold" />}
                  {pnlPositive ? '+' : ''}{formatINR(analytics.total_pnl)}
                </div>
                <div className={`text-xs font-mono mt-1 ${pnlPositive ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>{formatPercent(analytics.total_pnl_percent)}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-5">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Day Change</div>
                <div className={`text-3xl font-mono font-semibold ${dayPositive ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>
                  {dayPositive ? '+' : ''}{formatINR(analytics.day_change)}
                </div>
                <div className={`text-xs font-mono mt-1 ${dayPositive ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>{formatPercent(analytics.day_change_percent)}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-5" data-testid="risk-metrics">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2 flex items-center gap-1">
                  <ShieldCheck size={14} weight="duotone" /> Risk Metrics
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div><span className="text-[#64748B]">Beta</span><div className="text-white text-sm">{analytics.risk_metrics.beta}</div></div>
                  <div><span className="text-[#64748B]">Volatility</span><div className="text-white text-sm">{analytics.risk_metrics.volatility}%</div></div>
                  <div><span className="text-[#64748B]">Sharpe</span><div className="text-white text-sm">{analytics.risk_metrics.sharpe_ratio}</div></div>
                  <div><span className="text-[#64748B]">Diversity</span><div className="text-white text-sm">{analytics.risk_metrics.diversification_score}%</div></div>
                </div>
              </div>
            </div>

            {/* Sector Allocation + Holdings */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Sector Allocation Pie */}
              <div className="bg-[#12141A] border border-white/10 p-5" data-testid="sector-allocation">
                <div className="flex items-center gap-2 mb-4">
                  <ChartPieSlice size={20} weight="duotone" className="text-[#2563EB]" />
                  <h3 className="text-lg font-heading font-bold text-white">Sector Allocation</h3>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={analytics.sector_allocation}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {analytics.sector_allocation.map((entry, i) => (
                        <Cell key={entry.sector} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={PIE_TOOLTIP_STYLE} labelStyle={PIE_TOOLTIP_LABEL} formatter={(v, n, p) => [`${formatINR(v)} (${p.payload.percent}%)`, p.payload.sector]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {analytics.sector_allocation.map((s, i) => (
                    <div key={s.sector} className="flex items-center justify-between text-xs font-mono">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3" style={{ background: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                        <span className="text-white">{s.sector}</span>
                      </div>
                      <span className="text-[#94A3B8]">{s.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Holdings Table */}
              <div className="lg:col-span-2 bg-[#12141A] border border-white/10 overflow-hidden" data-testid="holdings-table">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-lg font-heading font-bold text-white">Holdings ({analytics.holdings.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-[#0A0B0E]/50">
                        <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Symbol</th>
                        <th className="text-right p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Qty</th>
                        <th className="text-right p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Avg</th>
                        <th className="text-right p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">LTP</th>
                        <th className="text-right p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Value</th>
                        <th className="text-right p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">P&L</th>
                        <th className="text-center p-3 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.holdings.map((h, i) => (
                        <tr key={`${h.symbol}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`holding-row-${h.symbol}`}>
                          <td className="p-3">
                            <Link to={`/stock/${h.symbol}`} className="font-mono font-semibold text-white hover:text-[#2563EB] transition-colors">{h.symbol}</Link>
                            <div className="text-xs text-[#64748B]">{h.sector}</div>
                          </td>
                          <td className="p-3 text-right font-mono text-white">{h.quantity}</td>
                          <td className="p-3 text-right font-mono text-[#94A3B8]">{formatINR(h.avg_price)}</td>
                          <td className="p-3 text-right font-mono text-white">{formatINR(h.current_price)}</td>
                          <td className="p-3 text-right font-mono text-white">{formatINR(h.current_value)}</td>
                          <td className="p-3 text-right">
                            <div className={`font-mono font-semibold ${h.pnl >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>
                              {h.pnl >= 0 ? '+' : ''}{formatINR(h.pnl)}
                            </div>
                            <div className={`text-xs font-mono ${h.pnl >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>{formatPercent(h.pnl_percent)}</div>
                          </td>
                          <td className="p-3 text-center">
                            <button
                              onClick={() => handleRemoveHolding(h.symbol)}
                              className="text-[#94A3B8] hover:text-[#FF3366] hover:bg-[#FF3366]/10 p-2 transition-colors"
                              title={`Remove ${h.symbol}`}
                              data-testid={`remove-holding-${h.symbol}`}
                            >
                              <Trash size={16} weight="regular" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-[#12141A] border border-white/10 p-12 text-center" data-testid="empty-portfolio">
            <Briefcase size={64} weight="duotone" className="mx-auto text-[#64748B] mb-4" />
            <p className="text-[#94A3B8]">No holdings yet. Add your first holding to see live P&L, sector allocation, and risk metrics.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
